/**
 * PropertyAutofillGroup — editor panel for property-bound fields on a
 * contract. The property LINK is hard: there is no freetext-only mode
 * (you must pick an existing property or create a new one). But the
 * VALUES the link snapshots into `contract.values` can be adjusted
 * per-contract — sometimes the rendered address differs from the
 * property's catalog entry (apartment-specific c/o lines, alternate
 * spelling, etc.). The link stays; only the rendered text diverges.
 * Unlinking blanks property fields; re-linking overwrites with the
 * latest catalog snapshot.
 *
 * Soft-duplicate detection is server-side. When create returns
 * `warnings.soft_duplicates`, we show a yellow banner listing each
 * candidate with a one-click "Länka istället" — the most common
 * mistake is creating a "Storgatan 1 b" when "Storgatan 1" already
 * exists, and we want a fast escape hatch.
 */

import { useCallback, useEffect, useState } from 'react';
import { Building2, Link2, AlertTriangle, X } from 'lucide-react';
import {
  RENDERERS,
  type VariableField,
} from '@hyranai/regta-templates';
import { composePostadress } from '@regta/common/postadress';
import {
  tenantApi,
  type PropertyAddress,
  type PropertySearchHit,
  type SoftDupProperty,
} from '../../api.js';
import { EntityCombobox, type EntityComboboxOption } from '../EntityCombobox.js';
import { locateAnchor } from '../../lib/locate-anchor.js';

/** Single-line postadress ("street, postnr ort") — canonical formatter. */
function composeAddress(a: { street: string; postalCode: string | null; ort: string | null }): string {
  return composePostadress(a) ?? '';
}

export function PropertyAutofillGroup({
  token,
  fields,
  values,
  linkedId,
  defaultGroupId,
  onLink,
  onUnlink,
  onFieldChange,
  disabled,
  sectionPrefix,
  chromeless = false,
}: {
  token: string;
  fields: VariableField<Record<string, unknown>>[];
  values: Record<string, unknown>;
  linkedId: string | null;
  /**
   * When true, render without the outer fieldset/legend chrome. Use this
   * when the property block is embedded inside another panel that
   * already owns the section header (e.g. injected inside the §3
   * Hyresobjekt panel).
   */
  chromeless?: boolean;
  /** "§3"-style prefix (per ADR-0049) showing which document section
   *  this property realises. Optional — legacy templates omit it. */
  sectionPrefix?: string;
  /** Group new properties will land in if the user clicks "Skapa ny". */
  defaultGroupId: string | null;
  onLink: (propertyId: string) => Promise<void>;
  onUnlink: () => Promise<void>;
  /**
   * Override the auto-snapshotted value for a property-bound field.
   * The property link stays; only the rendered text on this contract
   * diverges. Re-linking the property overwrites overrides.
   */
  onFieldChange: (key: string, value: unknown) => void;
  disabled: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftStreet, setDraftStreet] = useState('');
  const [draftPostal, setDraftPostal] = useState('');
  const [draftOrt, setDraftOrt] = useState('');
  const [draftKommun, setDraftKommun] = useState('');
  const [busy, setBusy] = useState(false);
  const [softDups, setSoftDups] = useState<SoftDupProperty[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<PropertyAddress[]>([]);

  // The contract field that holds the rendered property address — the
  // field declared `autofill: { kind: 'property', field: 'address' }`.
  // Fall back to the conventional key when the template doesn't expose it.
  const addressKey =
    fields.find(
      (f) => f.autofill?.kind === 'property' && f.autofill.field === 'address',
    )?.key ?? 'property_address';

  // Load the linked property's address list so we can offer a picker when
  // the fastighet spans several gatuadresser (T-10 / ADR-0047).
  useEffect(() => {
    let cancelled = false;
    if (!linkedId) {
      setAddresses([]);
      return;
    }
    void tenantApi.properties
      .get(linkedId)
      .then((d) => {
        if (!cancelled) setAddresses(d.addresses);
      })
      .catch(() => {
        if (!cancelled) setAddresses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [linkedId]);

  const currentAddress = (values[addressKey] as string | undefined) ?? '';
  const addressOptions = addresses.map((a) => composeAddress(a));
  const matchesOption = addressOptions.some((o) => o === currentAddress);

  const search = useCallback(
    async (q: string): Promise<EntityComboboxOption<PropertySearchHit>[]> => {
      const r = await tenantApi.properties.search({ q: q.trim() || undefined });
      return r.properties.map((p) => ({
        id: p.id,
        primary: p.name,
        secondary:
          [p.street, [p.postalCode, p.ort].filter(Boolean).join(' ').trim()]
            .filter(Boolean)
            .join(' · ') || null,
        value: p,
      }));
    },
    [token],
  );

  const submitCreate = async () => {
    if (!defaultGroupId) {
      setErr('Ingen grupp att skapa i.');
      return;
    }
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await tenantApi.properties.create({
        group_id: defaultGroupId,
        name,
        street: draftStreet.trim() || null,
        postal_code: draftPostal.trim() || null,
        ort: draftOrt.trim() || null,
        kommun: draftKommun.trim() || null,
      });
      if (r.warnings?.soft_duplicates?.length) {
        setSoftDups(r.warnings.soft_duplicates);
      }
      // Link the freshly-created property immediately.
      await onLink(r.property.id);
      setCreating(false);
      setDraftName('');
      setDraftStreet('');
      setDraftPostal('');
      setDraftOrt('');
      setDraftKommun('');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const linkInstead = async (candidateId: string) => {
    // The user chose an existing candidate after creating a duplicate.
    // We swap the link to the candidate and leave the just-created row
    // in place — it's harmless and the user may have meant it. (No
    // delete-on-undo for now; that's a follow-up.)
    await onLink(candidateId);
    setSoftDups(null);
  };

  const Wrapper = chromeless ? 'div' : 'fieldset';
  const wrapperClass = chromeless
    ? 'flex flex-col gap-3'
    : 'flex flex-col gap-3 rounded border border-stone-200 p-3';

  return (
    <Wrapper className={wrapperClass}>
      {!chromeless && (
        <legend className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
          <Building2 className="h-3.5 w-3.5" />
          {sectionPrefix && <span className="text-stone-400">{sectionPrefix}</span>}
          Fastighet
          <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[9px] normal-case tracking-normal text-stone-500">
            Obligatorisk
          </span>
        </legend>
      )}

      {err && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">{err}</div>
      )}

      {softDups && softDups.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium">Möjligt duplikat</div>
              <div className="mt-1 text-amber-800">
                Liknande fastigheter finns redan. Vill du länka till någon av dessa istället?
              </div>
              <ul className="mt-2 space-y-1">
                {softDups.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2">
                    <div className="truncate">
                      <span className="font-medium">{c.name}</span>
                      {c.street && (
                        <span className="ml-1 text-amber-700">· {c.street}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => void linkInstead(c.id)}
                      className="shrink-0 rounded bg-amber-200 px-2 py-0.5 text-amber-900 hover:bg-amber-300"
                    >
                      Länka istället
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setSoftDups(null)}
                className="mt-2 text-amber-700 hover:underline"
              >
                Behåll den nya
              </button>
            </div>
          </div>
        </div>
      )}

      {linkedId ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded bg-green-50 px-3 py-2 text-xs">
            <div className="flex items-center gap-2 text-green-800">
              <Link2 className="h-3.5 w-3.5" />
              <span className="font-medium">
                {currentAddress.split('\n')[0] || 'Länkad fastighet'}
              </span>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={() => void onUnlink()}
                className="inline-flex items-center gap-1 rounded border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                title="Ångra valet — t.ex. om du råkat länka fel fastighet"
              >
                <X className="h-3 w-3" />
                Avbryt
              </button>
            )}
          </div>

          {addresses.length > 1 && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-stone-600">
                Adress (fastigheten har flera)
              </label>
              <select
                value={matchesOption ? currentAddress : '__custom__'}
                disabled={disabled}
                onChange={(e) => {
                  if (e.target.value === '__custom__') return;
                  onFieldChange(addressKey, e.target.value);
                }}
                className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm disabled:opacity-50"
              >
                {!matchesOption && (
                  <option value="__custom__">Egen adress</option>
                )}
                {addresses.map((a) => {
                  const composed = composeAddress(a);
                  return (
                    <option key={a.id} value={composed}>
                      {composed}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      ) : !disabled ? (
        creating ? (
          <div className="space-y-2 rounded border border-stone-200 bg-stone-50 p-3">
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Beteckning (t.ex. Klockaren 5)"
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <input
              value={draftStreet}
              onChange={(e) => setDraftStreet(e.target.value)}
              placeholder="Adress, t.ex. Ångvägen 1-2"
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <input
                value={draftPostal}
                onChange={(e) => setDraftPostal(e.target.value)}
                placeholder="114 44"
                className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
              />
              <input
                value={draftOrt}
                onChange={(e) => setDraftOrt(e.target.value)}
                placeholder="Stockholm"
                className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
              />
            </div>
            <input
              value={draftKommun}
              onChange={(e) => setDraftKommun(e.target.value)}
              placeholder="Kommun, t.ex. Stockholm"
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setDraftName('');
                  setDraftStreet('');
                  setDraftPostal('');
                  setDraftOrt('');
                  setDraftKommun('');
                }}
                className="rounded px-3 py-1 text-xs font-medium text-stone-600"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={() => void submitCreate()}
                disabled={busy || draftName.trim().length === 0 || !defaultGroupId}
                className="rounded bg-stone-900 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
              >
                {busy ? 'Skapar…' : 'Skapa & länka'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <EntityCombobox
              placeholder="Sök fastighet…"
              onSearch={search}
              onPick={(opt) => void onLink(opt.id)}
              emptyHint="Inga fastigheter. Skapa en ny nedan."
            />
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="text-xs text-stone-600 hover:underline"
            >
              + Skapa ny fastighet
            </button>
          </div>
        )
      ) : null}

      <div className="flex flex-col gap-3">
        {fields.map((f) => {
          const Renderer = RENDERERS[f.type];
          // Editable even when linked — the link is hard, but the
          // rendered text can drift per-contract. Disabled only when
          // the contract itself is no longer a draft, or when no
          // property is linked yet (nothing to render until then) —
          // UNLESS the field opts into `propertyFreetext`, in which case
          // it stays editable before a link too (linking still prefills /
          // overwrites). Used by one-page forms whose address should be
          // typeable without forcing a Fastighet pick.
          return (
            <div key={f.key} data-locate-anchor={locateAnchor('field', f.key)}>
              <Renderer
                field={f}
                value={values[f.key]}
                onChange={(next) => onFieldChange(f.key, next)}
                disabled={
                  disabled || (linkedId === null && !f.propertyFreetext)
                }
              />
            </div>
          );
        })}
      </div>
    </Wrapper>
  );
}
